/**
 * ModuleStatuses - reusable statuses manager scoped to one module.
 * Props:
 *   moduleId   (number) - 1=WorkOrders, 2=Violations, 3=Architectural
 *   moduleName (string) - display label, e.g. "Work Orders"
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, IconButton, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Switch,
  FormControlLabel, Tooltip, Snackbar, Alert, Chip,
} from "@mui/material";
import AddIcon         from "@mui/icons-material/Add";
import EditIcon        from "@mui/icons-material/Edit";
import DeleteIcon      from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon        from "@mui/icons-material/PlayArrow";
import PauseCircleIcon      from "@mui/icons-material/PauseCircle";
import AutorenewIcon        from "@mui/icons-material/Autorenew";
import InfoIcon             from "@mui/icons-material/Info";
import WarningAmberIcon     from "@mui/icons-material/WarningAmber";
import CancelIcon           from "@mui/icons-material/Cancel";
import HourglassEmptyIcon   from "@mui/icons-material/HourglassEmpty";
import DoneAllIcon          from "@mui/icons-material/DoneAll";
import LoopIcon             from "@mui/icons-material/Loop";
import api from "../../utils/api";

// ── Icon helpers ──────────────────────────────────────────────────────────────
const ICON_OPTIONS = [
  { label: "Play",        value: "PlayArrow",      icon: <PlayArrowIcon fontSize="small" /> },
  { label: "Pause",       value: "PauseCircle",    icon: <PauseCircleIcon fontSize="small" /> },
  { label: "Check",       value: "CheckCircle",    icon: <CheckCircleIcon fontSize="small" /> },
  { label: "Refresh",     value: "Autorenew",      icon: <AutorenewIcon fontSize="small" /> },
  { label: "Info",        value: "Info",           icon: <InfoIcon fontSize="small" /> },
  { label: "Warning",     value: "WarningAmber",   icon: <WarningAmberIcon fontSize="small" /> },
  { label: "Cancel",      value: "Cancel",         icon: <CancelIcon fontSize="small" /> },
  { label: "Hourglass",   value: "HourglassEmpty", icon: <HourglassEmptyIcon fontSize="small" /> },
  { label: "Done All",    value: "DoneAll",        icon: <DoneAllIcon fontSize="small" /> },
  { label: "Loop",        value: "Loop",           icon: <LoopIcon fontSize="small" /> },
];

const COLOR_OPTIONS = [
  { label: "Primary",    value: "primary"   },
  { label: "Secondary",  value: "secondary" },
  { label: "Success",    value: "success"   },
  { label: "Error",      value: "error"     },
  { label: "Warning",    value: "warning"   },
  { label: "Info",       value: "info"      },
  { label: "Grey",       value: "grey"      },
  { label: "BlueGrey",   value: "blueGrey"  },
  { label: "Light Green",value: "lightGreen"},
];

const COLOR_MAP = {
  primary:    "#1976d2", secondary: "#9c27b0", success: "#2e7d32",
  error:      "#d32f2f", warning:   "#ed6c02", info:    "#0288d1",
  grey:       "#9e9e9e", blueGrey:  "#607d8b", lightGreen: "#8bc34a",
};

const getIcon = (value) => {
  const m = ICON_OPTIONS.find(i => i.value === value);
  return m ? m.icon : null;
};

const BLANK = {
  statusname: "", alert_time: "", icon: "", color: "primary",
  is_billable_trigger: false, show_on_member_page: true,
};

// ── Styles ────────────────────────────────────────────────────────────────────
const paperSx = {
  borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)",
  boxShadow:"0 4px 16px rgba(0,0,0,0.05)", overflow:"hidden",
};
const primaryBtnSx = {
  backgroundColor:"#2f63ad", color:"white", fontWeight:700,
  borderRadius:"10px", textTransform:"none", boxShadow:"none",
  "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" },
};
const fieldSx = { mt:1.5, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };

// ── Component ─────────────────────────────────────────────────────────────────
const ModuleStatuses = ({ moduleId, moduleName }) => {
  const [statuses, setStatuses] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [open,     setOpen]     = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [form,     setForm]     = useState(BLANK);
  const [snack,    setSnack]    = useState({ open:false, msg:"", sev:"success" });

  const showSnack = (msg, sev="success") => setSnack({ open:true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // include_inactive=1 so admins can see and re-activate deactivated statuses
      const res = await api.get(`/statuses?module_id=${moduleId}&include_inactive=1`);
      setStatuses(res.data || []);
    } catch {
      showSnack("Failed to load statuses.", "error");
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditId(null);
    setForm(BLANK);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      statusname:           s.statusname || "",
      alert_time:           s.alert_time != null ? String(s.alert_time) : "",
      icon:                 s.icon || "",
      color:                s.color || "primary",
      is_billable_trigger:  Boolean(s.is_billable_trigger),
      show_on_member_page:  s.show_on_member_page !== 0 && s.show_on_member_page !== false,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.statusname.trim()) { showSnack("Status name is required.", "error"); return; }
    const payload = {
      module_id:          moduleId,
      statusname:         form.statusname.trim(),
      alert_time:         form.alert_time !== "" ? Number(form.alert_time) : 0,
      icon:               form.icon || null,
      color:              form.color || "primary",
      is_billable_trigger: form.is_billable_trigger ? 1 : 0,
      show_on_member_page: form.show_on_member_page ? 1 : 0,
    };
    try {
      if (editId) {
        await api.put(`/statuses/${editId}`, payload);
        showSnack("Status updated.");
      } else {
        await api.post("/statuses", payload);
        showSnack("Status created.");
      }
      setOpen(false);
      load();
    } catch {
      showSnack("Failed to save status.", "error");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove status "${name}"?\n\nIf this status is used on existing records it will be deactivated (hidden from dropdowns) but not deleted, so historical data stays accurate.\n\nIf it has never been used it will be permanently deleted.`)) return;
    try {
      const res = await api.delete(`/statuses/${id}`);
      if (res.data?.soft_deleted) {
        showSnack(res.data.message, "warning");
      } else {
        showSnack("Status permanently deleted.");
      }
      load();
    } catch {
      showSnack("Failed to remove status.", "error");
    }
  };

  const handleReactivate = async (id, name) => {
    try {
      await api.put(`/statuses/${id}`, { is_active: 1 });
      showSnack(`Status "${name}" reactivated.`);
      load();
    } catch {
      showSnack("Failed to reactivate status.", "error");
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>
            {moduleName} Statuses
          </Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Manage the status lifecycle for {moduleName}. Changes apply immediately across the app.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={primaryBtnSx}>
          Add Status
        </Button>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={paperSx}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
              <TableRow>
                {["Status Name","Icon","Color","Alert Time (days)","Billable Trigger","Member Visible","Actions"].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign:"center", py:4, color:"#6b778c" }}>Loading...</TableCell></TableRow>
              ) : statuses.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign:"center", py:4, color:"#6b778c" }}>
                  No statuses yet. Click "Add Status" to create one.
                </TableCell></TableRow>
              ) : statuses.map(s => {
                const inactive = s.is_active === 0 || s.is_active === false;
                return (
                  <TableRow key={s.id} hover sx={{ opacity: inactive ? 0.5 : 1 }}>
                    <TableCell>
                      <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                        <Typography sx={{ fontWeight:600, color:"#223142", textDecoration: inactive ? "line-through" : "none" }}>
                          {s.statusname}
                        </Typography>
                        {inactive && (
                          <Chip size="small" label="Deactivated"
                            sx={{ fontWeight:700, fontSize:10, borderRadius:"6px", bgcolor:"rgba(0,0,0,0.06)", color:"#6b778c" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {s.icon ? (
                        <Tooltip title={s.icon}>
                          <Box sx={{ display:"inline-flex", color: COLOR_MAP[s.color] || "#1976d2" }}>
                            {getIcon(s.icon)}
                          </Box>
                        </Tooltip>
                      ) : <Typography variant="caption" sx={{ color:"#9ca3af" }}>None</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.color || "primary"}
                        sx={{ fontWeight:600, fontSize:11, borderRadius:"8px",
                          backgroundColor: `${COLOR_MAP[s.color] || "#1976d2"}20`,
                          color: COLOR_MAP[s.color] || "#1976d2" }} />
                    </TableCell>
                    <TableCell sx={{ color:"#6b778c" }}>{s.alert_time ?? "N/A"}</TableCell>
                    <TableCell>
                      {s.is_billable_trigger ? (
                        <Chip size="small" label="Yes" sx={{ fontWeight:700, fontSize:11, bgcolor:"rgba(34,197,94,0.12)", color:"#16a34a", borderRadius:"8px" }} />
                      ) : (
                        <Typography variant="caption" sx={{ color:"#9ca3af" }}>No</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.show_on_member_page === 0 || s.show_on_member_page === false ? (
                        <Chip size="small" label="Hidden" sx={{ fontWeight:700, fontSize:11, bgcolor:"rgba(220,38,38,0.09)", color:"#dc2626", borderRadius:"8px" }} />
                      ) : (
                        <Chip size="small" label="Visible" sx={{ fontWeight:700, fontSize:11, bgcolor:"rgba(34,197,94,0.12)", color:"#16a34a", borderRadius:"8px" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display:"flex", gap:0.5 }}>
                        {inactive ? (
                          <Tooltip title="Reactivate">
                            <IconButton size="small" color="success" onClick={() => handleReactivate(s.id, s.statusname)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(s)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={inactive ? "Permanently delete" : "Deactivate / Delete"}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(s.id, s.statusname)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142" }}>
          {editId ? "Edit Status" : `Add ${moduleName} Status`}
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Status Name *" value={form.statusname}
            onChange={e => setForm(f => ({...f, statusname: e.target.value}))} sx={fieldSx} />

          <TextField fullWidth label="Alert Time (days)" type="number"
            inputProps={{ min:0 }} value={form.alert_time}
            onChange={e => setForm(f => ({...f, alert_time: e.target.value}))}
            helperText="Optional - how many days before an alert is triggered"
            sx={fieldSx} />

          <TextField select fullWidth label="Icon" value={form.icon}
            onChange={e => setForm(f => ({...f, icon: e.target.value}))} sx={fieldSx}>
            <MenuItem value="">None</MenuItem>
            {ICON_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>{o.icon}{o.label}</Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField select fullWidth label="Color" value={form.color}
            onChange={e => setForm(f => ({...f, color: e.target.value}))} sx={fieldSx}>
            {COLOR_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                  <Box sx={{ width:14, height:14, borderRadius:"50%", bgcolor: COLOR_MAP[o.value] }} />
                  {o.label}
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ mt:2, px:1.5, py:1, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.08)" }}>
            <FormControlLabel
              control={<Switch checked={form.is_billable_trigger}
                onChange={e => setForm(f => ({...f, is_billable_trigger: e.target.checked}))} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight:600 }}>Billable Trigger</Typography>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>
                    Automatically creates a billing record when a record moves to this status
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box sx={{ mt:1.5, px:1.5, py:1, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.08)" }}>
            <FormControlLabel
              control={<Switch checked={form.show_on_member_page}
                onChange={e => setForm(f => ({...f, show_on_member_page: e.target.checked}))} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight:600 }}>Visible on Member Page</Typography>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>
                    Turn off to hide records in this status from homeowners on their Owners Page portal (e.g. Void). Staff always see every status.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, pt:1, gap:1 }}>
          <Button onClick={() => setOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={primaryBtnSx}>
            {editId ? "Update" : "Add Status"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack(s => ({...s, open:false}))}
        anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({...s, open:false}))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModuleStatuses;
